import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase, CmsBusiness } from '@/lib/supabase';
import { businesses as mockBusinesses } from '@/mocks/businessData';
import { useScrollAnimation, useStaggerAnimation } from '@/hooks/useScrollAnimation';

export default function CoreBusiness() {
  const [businesses, setBusinesses] = useState<CmsBusiness[]>([]);
  const [loading, setLoading] = useState(true);

  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: gridRef, visibleItems } = useStaggerAnimation(8, { threshold: 0.05 });

  useEffect(() => {
    const fetchBusinesses = async () => {
      const { data, error } = await supabase
        .from('cms_business')
        .select('id, title, description, icon, slug, image_url, sort_order')
        .eq('is_published', true)
        .order('sort_order', { ascending: true });

      if (error || !data || data.length === 0) {
        // fallback to mock
        setBusinesses(
          mockBusinesses.map((b, i) => ({
            id: i + 1,
            title: b.title,
            description: b.description,
            icon: b.icon,
            slug: b.id,
            image_url: b.image,
            hero_bg_url: b.heroBg,
            detail_intro: b.detailIntro,
            highlights: b.highlights,
            features: b.features,
            cases_data: b.cases,
            sort_order: i + 1,
            is_published: true,
            created_at: '',
            updated_at: '',
          }))
        );
      } else {
        setBusinesses(data as CmsBusiness[]);
      }
      setLoading(false);
    };

    fetchBusinesses();
  }, []);

  const displayList = loading
    ? mockBusinesses.map((b, i) => ({
        id: i + 1,
        title: b.title,
        description: b.description,
        icon: b.icon,
        slug: b.id,
        image_url: b.image,
        hero_bg_url: b.heroBg,
        detail_intro: b.detailIntro,
        highlights: b.highlights,
        features: b.features,
        cases_data: b.cases,
        sort_order: i + 1,
        is_published: true,
        created_at: '',
        updated_at: '',
      }))
    : businesses;

  return (
    <section id="business" className="py-10 md:py-20 lg:py-24 bg-gray-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div
          ref={headerRef}
          className="flex flex-col lg:flex-row justify-between items-start lg:items-end mb-8 md:mb-16 gap-3 md:gap-6"
          style={{
            transition: 'opacity 0.7s ease, transform 0.7s ease',
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(40px)',
          }}
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">核心业务</h2>
          <p className="text-xs sm:text-sm md:text-base lg:text-lg text-gray-600 lg:text-right max-w-xl leading-relaxed">
            覆盖算力运营、AI装备、工业数字化、智慧城市等八大核心领域
          </p>
        </div>

        <div ref={gridRef} className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
          {displayList.map((business, index) => (
            <Link
              to={`/business/${business.slug}`}
              key={business.id}
              className="group bg-white rounded-xl md:rounded-2xl overflow-hidden hover:shadow-xl hover:-translate-y-1 cursor-pointer block"
              style={{
                transition: `opacity 0.7s ease ${index * 80}ms, transform 0.7s ease ${index * 80}ms, box-shadow 0.3s ease`,
                opacity: visibleItems[index] ? 1 : 0,
                transform: visibleItems[index] ? 'translateY(0)' : 'translateY(48px)',
              }}
            >
              <div className="relative h-28 sm:h-36 md:h-44 overflow-hidden">
                <img
                  src={business.image_url}
                  alt={business.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                <div className="absolute top-2.5 left-2.5 md:top-4 md:left-4 w-8 h-8 md:w-10 md:h-10 bg-white/90 backdrop-blur-sm rounded-lg md:rounded-xl flex items-center justify-center">
                  <i className={`${business.icon} text-base md:text-xl text-teal-600`}></i>
                </div>
              </div>
              <div className="p-3 sm:p-4 md:p-5">
                <h3 className="text-xs sm:text-sm md:text-base font-bold text-gray-900 mb-1.5 md:mb-2">{business.title}</h3>
                <p className="text-xs text-gray-600 leading-relaxed mb-2.5 md:mb-4 line-clamp-2 md:line-clamp-3">{business.description}</p>
                <span className="inline-flex items-center text-xs text-teal-600 font-medium group-hover:text-teal-700 whitespace-nowrap">
                  了解详情
                  <i className="ri-arrow-right-line ml-1 group-hover:translate-x-1 transition-transform"></i>
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
